import React from "react";
import { ChevronDown, Layers, MonitorSmartphone, MousePointerClick, ShieldCheck, Sparkles } from "lucide-react";
import { useReveal } from "../../hooks/useReveal";

const FAQ = [
  {
    q: "Does this test real mobile devices?",
    a: "No. WebPreview loads your site inside a browser iframe sized to match common device viewports. It's excellent for catching layout, spacing, and breakpoint issues, but it runs on your desktop browser's engine — not real device hardware.",
  },
  {
    q: "Why won't some websites load in the preview?",
    a: "Sites can send an X-Frame-Options header or a Content-Security-Policy frame-ancestors directive that blocks embedding in an iframe for security reasons. This is a deliberate browser security feature, and WebPreview does not attempt to bypass it.",
  },
  {
    q: "Is my browsing data sent anywhere?",
    a: "No. URLs, history, favorites, custom devices, and sessions are stored only in your browser's localStorage. Nothing is sent to a server — WebPreview has no backend.",
  },
  {
    q: "Can I test touch interactions or mobile gestures?",
    a: "Not accurately. The iframe reproduces viewport dimensions only. Touch behavior, mobile browser quirks, and OS-level rendering differences are not simulated.",
  },
];

const FEATURES = [
  {
    icon: Layers,
    title: "What it does",
    text: "Load a URL once and preview it across multiple viewport sizes simultaneously.",
  },
  {
    icon: MousePointerClick,
    title: "How to use it",
    text: "Enter a URL, select devices from the sidebar, then rotate, zoom, or reload individually.",
  },
  {
    icon: MonitorSmartphone,
    title: "Supported previews",
    text: "Any public site that allows iframe embedding, plus localhost and LAN addresses.",
  },
];

// Wraps a block so it fades/slides in the first time it scrolls into view.
function Reveal({ as: Tag = "div", className = "", delay = 0, children, ...rest }) {
  const { ref, visible } = useReveal();
  return (
    <Tag
      ref={ref}
      className={`reveal${visible ? " is-visible" : ""}${className ? ` ${className}` : ""}`}
      style={{ "--reveal-delay": `${delay}ms` }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

function InfoSection() {
  return (
    <section className="info-section" id="info-section" aria-labelledby="info-heading">
      <div className="info-glow" aria-hidden="true" />

      <Reveal className="info-hero">
        <span className="info-eyebrow">
          <Sparkles size={14} strokeWidth={2} aria-hidden="true" />
          Responsive testing, side by side
        </span>
        <h2 id="info-heading">
          Preview responsive layouts across <span className="text-gradient">common viewport sizes</span>
        </h2>
        <p>
          WebPreview renders your site inside scaled iframes sized to match popular phone, tablet,
          laptop, and desktop viewports, so you can compare layouts side by side without switching
          devices.
        </p>
      </Reveal>

      <div className="info-grid">
        {FEATURES.map(({ icon: Icon, title, text }, index) => (
          <Reveal key={title} className="info-card" delay={index * 90}>
            <span className="info-card-icon" aria-hidden="true">
              <Icon size={20} strokeWidth={1.75} />
            </span>
            <h3>{title}</h3>
            <p>{text}</p>
          </Reveal>
        ))}
      </div>

      <Reveal className="info-limitations">
        <span className="info-limitations-icon" aria-hidden="true">
          <ShieldCheck size={20} strokeWidth={1.75} />
        </span>
        <div>
          <h3>What this tool does not do</h3>
          <p>
            WebPreview simulates viewport sizes inside your desktop browser — it does not fully
            reproduce real mobile browser behavior, device hardware, touch input, mobile operating
            systems, browser-specific rendering engines, or real network conditions.
          </p>
        </div>
      </Reveal>

      <Reveal className="info-faq">
        <h3>Frequently asked questions</h3>
        <div className="faq-list">
          {FAQ.map((item) => (
            <details className="faq-item" key={item.q}>
              <summary>
                <span>{item.q}</span>
                <ChevronDown className="faq-chevron" size={18} strokeWidth={2} aria-hidden="true" />
              </summary>
              <p>{item.a}</p>
            </details>
          ))}
        </div>
      </Reveal>

      <p className="disclaimer">
        Note: some websites block iframe embedding and will not load in the preview. For the most
        accurate desktop layout comparison, use a browser window at least 1366px wide.
      </p>
    </section>
  );
}

export default InfoSection;
